import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusIcon, SaveIcon, ArrowLeftIcon, LockIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, LayoutListIcon } from 'lucide-react';
import {
  DndContext,
  closestCorners,
  DragOverlay,
  pointerWithin,
  type CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  useFeedbackById,
  useFeedbackStructure,
  useFeedbackVersions,
  useCreateFeedback,
  useUpdateFeedback,
  useUpdateFeedbackStructure,
  usePublishVersion,
} from '../hooks/useFeedbackApi';
import { Question as BackendQuestion, Page as BackendPage, Panel as BackendPanel } from '../services/feedbackService';
import { SortableQuestionCard } from '../components/feedback-editor/SortableQuestionCard';
import { SortableSectionContainer } from '../components/feedback-editor/SortableSectionContainer';
import { QuestionCard } from '../components/feedback-editor/QuestionCard';
import { SectionContainer } from '../components/feedback-editor/SectionContainer';
import {
  useEditorDragAndDrop,
  flattenQuestions,
  buildItems,
} from '../components/feedback-editor/useEditorDragAndDrop';
import type { Question, Section, VisibilityCondition, EditorItem, FlagConfig } from '../components/feedback-editor/types';

const ELIGIBLE_TRIGGER_TYPES = ['multipleChoice', 'checkbox', 'rating'];

const normalizeQuestionType = (backendType: string): string => {
  switch (backendType) {
    case 'radiogroup':
    case 'dropdown':
    case 'boolean':
      return 'multipleChoice';
    case 'comment':
    case 'number':
    case 'email':
      return 'text';
    default:
      return backendType;
  }
};

const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return closestCorners(args);
};

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

const LoadingSkeleton: React.FC = () => (
  <div className="bg-gray-50 dark:bg-gray-900 min-h-full">
    <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="h-9 w-9" />
          <SkeletonBlock className="h-7 w-48" />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonBlock className="h-9 w-24 rounded-lg" />
          <SkeletonBlock className="h-9 w-36 rounded-lg" />
        </div>
      </div>
    </div>
    <div className="px-6 py-6 space-y-6 max-w-5xl mx-auto">
      <SkeletonBlock className="h-12 w-full rounded-lg" />
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 space-y-4">
        <SkeletonBlock className="h-10 w-full" />
        <SkeletonBlock className="h-20 w-full" />
      </div>
      <div className="flex items-center gap-3">
        <SkeletonBlock className="h-5 w-24" />
        <SkeletonBlock className="h-5 w-8 rounded-full" />
      </div>
      {[1, 2].map(i => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-3 flex items-center gap-3">
            <SkeletonBlock className="h-5 w-5 rounded-full" />
            <SkeletonBlock className="h-5 w-6 rounded-full" />
            <SkeletonBlock className="h-5 w-20 rounded-full" />
          </div>
          <div className="p-6 space-y-4">
            <SkeletonBlock className="h-10 w-full" />
            <SkeletonBlock className="h-10 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FeedbackEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const { data: feedback, isLoading: feedbackLoading } = useFeedbackById(id);
  const { data: versions } = useFeedbackVersions(id);
  const hasVersion = feedback?.currentVersion && feedback.currentVersion > 0;
  const { data: structure, isLoading: structureLoading } = useFeedbackStructure(
    id,
    selectedVersion || 1
  );
  const createFeedback = useCreateFeedback();
  const updateFeedback = useUpdateFeedback();
  const updateStructure = useUpdateFeedbackStructure();
  const publishVersion = usePublishVersion();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    items,
    setItems,
    topLevelIds,
    allQuestions,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getActiveQuestion,
    getActiveSection,
  } = useEditorDragAndDrop();

  const currentVersionInfo = versions?.find(v => v.versionNo === selectedVersion);
  const isViewingPublishedVersion = !!currentVersionInfo?.publishedAt;
  const isReadOnly = isViewingPublishedVersion;
  const latestVersion = versions && versions.length > 0
    ? Math.max(...versions.map(v => v.versionNo))
    : 1;
  const nextVersionNumber = latestVersion + 1;

  useEffect(() => {
    if (versions && versions.length > 0 && selectedVersion === null) {
      const latestDraft = versions.find(v => !v.publishedAt);
      if (latestDraft) {
        setSelectedVersion(latestDraft.versionNo);
      } else {
        setSelectedVersion(Math.max(...versions.map(v => v.versionNo)));
      }
    }
  }, [versions, selectedVersion]);

  const loadBackendQuestion = (
    q: BackendQuestion,
    allBackendQuestions: BackendQuestion[],
    sectionId: string | undefined
  ): Question => {
    const editorType = normalizeQuestionType(q.type);
    const options = q.choices?.map((c) => c.text) || [];
    if (q.type === 'boolean' && options.length === 0) {
      options.push('Yes', 'No');
    }

    const loaded: Question = {
      id: q.id || `temp-${Date.now()}-${Math.random()}`,
      type: editorType,
      text: q.title,
      required: q.required,
      options: editorType === 'multipleChoice' || editorType === 'checkbox' ? options : [],
      ratingScale: editorType === 'rating' ? (q.config?.rateMax || 5) : undefined,
      sectionId,
    };

    if (q.config?.visibilityCondition) {
      const vc = q.config.visibilityCondition;
      const triggerIndex = allBackendQuestions.findIndex(
        bq => bq.name === vc.triggerQuestionName
      );
      if (triggerIndex >= 0) {
        const triggerChoices = allBackendQuestions[triggerIndex]?.choices || [];
        const resolvedValues = (vc.values || []).map((val: string) => {
          const match = val.match(/^option_(\d+)$/);
          if (match) {
            const choiceIndex = parseInt(match[1], 10) - 1;
            return triggerChoices[choiceIndex]?.text || val;
          }
          return val;
        });
        loaded.visibilityCondition = {
          triggerQuestionIndex: triggerIndex,
          operator: vc.operator,
          values: resolvedValues,
        };
      }
    }

    if (q.config?.flag) {
      const flag = q.config.flag;
      const resolvedValues = (flag.condition.values || []).map((val: string) => {
        const match = val.match(/^option_(\d+)$/);
        if (match) {
          const choiceIndex = parseInt(match[1], 10) - 1;
          return q.choices?.[choiceIndex]?.text || val;
        }
        return val;
      });
      loaded.flagConfig = {
        label: flag.label,
        condition: { operator: flag.condition.operator, values: resolvedValues },
      };
    }

    return loaded;
  };

  useEffect(() => {
    if (structure) {
      setTitle(structure.versionTitle || '');
      setDescription(structure.versionDescription || '');

      if (structure.pages && structure.pages.length > 0) {
        const loadedSections: Section[] = [];
        const loadedQuestions: Question[] = [];

        for (const page of structure.pages) {
          const allPageQuestions = [
            ...page.questions,
            ...page.panels.flatMap(panel => panel.questions),
          ];

          for (const panel of (page.panels || [])) {
            const sectionId = panel.id || `section-${Date.now()}-${Math.random()}`;
            loadedSections.push({
              id: sectionId,
              name: panel.name,
              title: panel.title || '',
              description: panel.description || '',
              collapsed: false,
            });

            for (const q of panel.questions) {
              loadedQuestions.push(loadBackendQuestion(q, allPageQuestions, sectionId));
            }
          }

          for (const q of page.questions) {
            loadedQuestions.push(loadBackendQuestion(q, allPageQuestions, undefined));
          }
        }

        const defaultQuestion: Question = {
          id: `temp-${Date.now()}`,
          type: 'text',
          text: '',
          required: false,
        };
        const questionsToUse = loadedQuestions.length > 0 ? loadedQuestions : [defaultQuestion];
        setItems(buildItems(questionsToUse, loadedSections));
      } else {
        setItems([{
          kind: 'question',
          question: {
            id: `temp-${Date.now()}`,
            type: 'text',
            text: '',
            required: false,
          },
        }]);
      }
    } else if (!id) {
      setItems([{
        kind: 'question',
        question: {
          id: `temp-${Date.now()}`,
          type: 'text',
          text: '',
          required: false,
        },
      }]);
    }
  }, [structure, id]);

  const getGlobalIndex = useCallback((question: Question): number => {
    return allQuestions.findIndex(q => q.id === question.id);
  }, [allQuestions]);

  const handleAddQuestion = useCallback((sectionId?: string) => {
    const newQuestion: Question = {
      id: `temp-${Date.now()}`,
      type: 'text',
      text: '',
      required: false,
      sectionId,
    };

    setItems(prev => {
      if (sectionId) {
        return prev.map(item => {
          if (item.kind === 'section' && item.section.id === sectionId) {
            return { ...item, questions: [...item.questions, newQuestion] };
          }
          return item;
        });
      }
      return [...prev, { kind: 'question' as const, question: newQuestion }];
    });
  }, [setItems]);

  const handleAddSection = useCallback(() => {
    const sectionId = `section-${Date.now()}`;
    const newSection: Section = {
      id: sectionId,
      name: '',
      title: '',
      description: '',
      collapsed: false,
    };
    setItems(prev => [...prev, { kind: 'section' as const, section: newSection, questions: [] }]);
  }, [setItems]);

  const handleRemoveSection = useCallback((sectionId: string) => {
    setItems(prev => {
      const sectionItem = prev.find(it => it.kind === 'section' && it.section.id === sectionId);
      if (!sectionItem || sectionItem.kind !== 'section') {
        return prev.filter(it => !(it.kind === 'section' && it.section.id === sectionId));
      }

      const orphanedQuestions: EditorItem[] = sectionItem.questions.map(q => ({
        kind: 'question' as const,
        question: { ...q, sectionId: undefined },
      }));

      const sectionIndex = prev.indexOf(sectionItem);
      const newItems = [...prev];
      newItems.splice(sectionIndex, 1, ...orphanedQuestions);
      return newItems;
    });
  }, [setItems]);

  const handleSectionChange = useCallback((sectionId: string, field: keyof Section, value: any) => {
    setItems(prev => prev.map(item => {
      if (item.kind === 'section' && item.section.id === sectionId) {
        return { ...item, section: { ...item.section, [field]: value } };
      }
      return item;
    }));
  }, [setItems]);

  const handleToggleSectionCollapse = useCallback((sectionId: string) => {
    setItems(prev => prev.map(item => {
      if (item.kind === 'section' && item.section.id === sectionId) {
        return { ...item, section: { ...item.section, collapsed: !item.section.collapsed } };
      }
      return item;
    }));
  }, [setItems]);

  const updateQuestion = useCallback((questionId: string, updater: (q: Question) => Question) => {
    setItems(prev => prev.map(item => {
      if (item.kind === 'question' && item.question.id === questionId) {
        return { ...item, question: updater(item.question) };
      }
      if (item.kind === 'section') {
        const qIdx = item.questions.findIndex(q => q.id === questionId);
        if (qIdx >= 0) {
          const newQuestions = [...item.questions];
          newQuestions[qIdx] = updater(newQuestions[qIdx]);
          return { ...item, questions: newQuestions };
        }
      }
      return item;
    }));
  }, [setItems]);

  const updateAllQuestions = useCallback((updater: (questions: Question[]) => Question[]) => {
    setItems(prev => {
      const all = flattenQuestions(prev);
      const updated = updater(all);

      let idx = 0;
      return prev.map(item => {
        if (item.kind === 'question') {
          return { ...item, question: updated[idx++] };
        }
        const sectionQuestions = updated.slice(idx, idx + item.questions.length);
        idx += item.questions.length;
        return { ...item, questions: sectionQuestions };
      });
    });
  }, [setItems]);

  const handleRemoveQuestion = useCallback((questionId: string) => {
    setItems(prev => {
      const all = flattenQuestions(prev);
      const removedIndex = all.findIndex(q => q.id === questionId);

      const filteredAll = all
        .filter(q => q.id !== questionId)
        .map(q => {
          if (!q.visibilityCondition) return q;
          const vc = q.visibilityCondition;
          if (vc.triggerQuestionIndex === removedIndex) {
            const { visibilityCondition: _, ...rest } = q;
            return rest;
          }
          if (vc.triggerQuestionIndex > removedIndex) {
            return {
              ...q,
              visibilityCondition: { ...vc, triggerQuestionIndex: vc.triggerQuestionIndex - 1 },
            };
          }
          return q;
        });

      let idx = 0;
      const result: EditorItem[] = [];
      for (const item of prev) {
        if (item.kind === 'question') {
          if (item.question.id !== questionId) {
            result.push({ kind: 'question', question: filteredAll[idx++] });
          }
        } else {
          const count = item.questions.filter(q => q.id !== questionId).length;
          const sectionQuestions = filteredAll.slice(idx, idx + count);
          idx += count;
          result.push({ ...item, questions: sectionQuestions });
        }
      }
      return result;
    });
  }, [setItems]);

  const handleQuestionChange = useCallback((questionId: string, field: keyof Question, value: any) => {
    updateQuestion(questionId, q => ({ ...q, [field]: value }));
  }, [updateQuestion]);

  const handleOptionChange = useCallback((questionId: string, index: number, value: string) => {
    updateQuestion(questionId, q => {
      if (q.options) {
        const newOptions = [...q.options];
        newOptions[index] = value;
        return { ...q, options: newOptions };
      }
      return q;
    });
  }, [updateQuestion]);

  const handleAddOption = useCallback((questionId: string) => {
    updateQuestion(questionId, q => ({
      ...q,
      options: [...(q.options || []), ''],
    }));
  }, [updateQuestion]);

  const handleRemoveOption = useCallback((questionId: string, index: number) => {
    updateQuestion(questionId, q => {
      if (q.options) {
        const newOptions = [...q.options];
        newOptions.splice(index, 1);
        return { ...q, options: newOptions };
      }
      return q;
    });
  }, [updateQuestion]);

  const applyTypeDefaults = (q: Question): Question => {
    const updated = { ...q };
    if (q.type === 'multipleChoice' || q.type === 'checkbox') {
      updated.options = q.options || ['', ''];
      delete updated.ratingScale;
    } else if (q.type === 'rating') {
      updated.ratingScale = 5;
      delete updated.options;
    } else {
      delete updated.options;
      delete updated.ratingScale;
      delete updated.flagConfig;
    }
    return updated;
  };

  const handleTypeChange = useCallback((questionId: string, type: string) => {
    updateAllQuestions(questions => {
      const changedIndex = questions.findIndex(q => q.id === questionId);
      return questions.map((q, idx) => {
        if (q.visibilityCondition?.triggerQuestionIndex === changedIndex && !ELIGIBLE_TRIGGER_TYPES.includes(type)) {
          const { visibilityCondition: _, ...rest } = q;
          return idx === changedIndex ? applyTypeDefaults({ ...rest, type }) : rest;
        }
        if (q.id === questionId) {
          return applyTypeDefaults({ ...q, type });
        }
        return q;
      });
    });
  }, [updateAllQuestions]);

  const handleToggleCondition = useCallback((questionIndex: number) => {
    updateAllQuestions(questions =>
      questions.map((q, idx) => {
        if (idx !== questionIndex) return q;
        if (q.visibilityCondition) {
          const { visibilityCondition: _, ...rest } = q;
          return rest;
        }
        const eligibleIndex = questions.findIndex(
          (prev, i) => i < questionIndex && ELIGIBLE_TRIGGER_TYPES.includes(prev.type)
        );
        if (eligibleIndex < 0) return q;
        return {
          ...q,
          visibilityCondition: {
            triggerQuestionIndex: eligibleIndex,
            operator: 'anyOf' as const,
            values: [],
          },
        };
      })
    );
  }, [updateAllQuestions]);

  const handleConditionChange = useCallback((
    questionIndex: number,
    field: keyof VisibilityCondition,
    value: any
  ) => {
    updateAllQuestions(questions =>
      questions.map((q, idx) => {
        if (idx !== questionIndex || !q.visibilityCondition) return q;
        const updated = { ...q.visibilityCondition, [field]: value };
        if (field === 'triggerQuestionIndex') {
          updated.values = [];
        }
        return { ...q, visibilityCondition: updated };
      })
    );
  }, [updateAllQuestions]);

  const handleConditionValueToggle = useCallback((questionIndex: number, value: string) => {
    updateAllQuestions(questions =>
      questions.map((q, idx) => {
        if (idx !== questionIndex || !q.visibilityCondition) return q;
        const currentValues = q.visibilityCondition.values;
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return {
          ...q,
          visibilityCondition: { ...q.visibilityCondition, values: newValues },
        };
      })
    );
  }, [updateAllQuestions]);

  const handleToggleFlag = useCallback((questionId: string) => {
    updateQuestion(questionId, q => {
      if (q.flagConfig) {
        const { flagConfig: _, ...rest } = q;
        return rest as Question;
      }
      const flagConfig: FlagConfig = {
        label: '',
        condition: { operator: 'anyOf' as const, values: [] },
      };
      return {
        ...q,
        flagConfig,
      };
    });
  }, [updateQuestion]);

  const handleFlagChange = useCallback((questionId: string, field: string, value: any) => {
    updateQuestion(questionId, q => {
      if (!q.flagConfig) return q;
      if (field === 'label') {
        return { ...q, flagConfig: { ...q.flagConfig, label: value } };
      }
      if (field === 'condition.operator') {
        return { ...q, flagConfig: { ...q.flagConfig, condition: { ...q.flagConfig.condition, operator: value, values: [] } } };
      }
      if (field === 'condition.values') {
        return { ...q, flagConfig: { ...q.flagConfig, condition: { ...q.flagConfig.condition, values: value } } };
      }
      return q;
    });
  }, [updateQuestion]);

  const handleFlagValueToggle = useCallback((questionId: string, value: string) => {
    updateQuestion(questionId, q => {
      if (!q.flagConfig) return q;
      const current = q.flagConfig.condition.values;
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...q, flagConfig: { ...q.flagConfig, condition: { ...q.flagConfig.condition, values: newValues } } };
    });
  }, [updateQuestion]);

  const computeQuestionName = (q: Question, index: number): string => {
    return `${q.text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'question'}_${index + 1}`;
  };

  const convertQuestionsToStructure = (): BackendPage[] => {
    const questions = allQuestions;
    const questionNames = questions.map((q, index) => computeQuestionName(q, index));

    const toBackendQuestion = (q: Question, index: number): BackendQuestion => {
      const config: Record<string, any> = {};

      if (q.type === 'rating' && q.ratingScale) {
        config.rateMax = q.ratingScale;
      }

      if (q.visibilityCondition && q.visibilityCondition.values.length > 0) {
        const triggerQ = questions[q.visibilityCondition.triggerQuestionIndex];
        const conditionValues = q.visibilityCondition.values.map((text) => {
          const optIndex = triggerQ?.options?.indexOf(text) ?? -1;
          return optIndex >= 0 ? `option_${optIndex + 1}` : text;
        });
        config.visibilityCondition = {
          triggerQuestionName: questionNames[q.visibilityCondition.triggerQuestionIndex],
          operator: q.visibilityCondition.operator,
          values: conditionValues,
        };
      }

      if (q.flagConfig && q.flagConfig.label) {
        const conditionValues = (q.type === 'rating')
          ? q.flagConfig.condition.values
          : q.flagConfig.condition.values.map((text) => {
              const optIndex = q.options?.indexOf(text) ?? -1;
              return optIndex >= 0 ? `option_${optIndex + 1}` : text;
            });
        config.flag = {
          label: q.flagConfig.label,
          condition: { operator: q.flagConfig.condition.operator, values: conditionValues },
        };
      }

      return {
        id: q.id.startsWith('temp-') ? undefined : q.id,
        name: questionNames[index],
        title: q.text,
        helpText: '',
        type: q.type,
        required: q.required,
        position: index + 1,
        config: Object.keys(config).length > 0 ? config : undefined,
        choices:
          q.options?.map((option, optIndex) => ({
            position: optIndex + 1,
            value: `option_${optIndex + 1}`,
            text: option,
          })) || [],
      };
    };

    const ungroupedQuestions: BackendQuestion[] = [];
    const panels: BackendPanel[] = [];
    let positionCounter = 1;

    for (const item of items) {
      if (item.kind === 'question') {
        const globalIdx = getGlobalIndex(item.question);
        const bq = toBackendQuestion(item.question, globalIdx);
        bq.position = positionCounter++;
        ungroupedQuestions.push(bq);
      } else {
        if (item.questions.length === 0) continue;

        const sectionQuestions = item.questions.map(q => {
          const globalIdx = getGlobalIndex(q);
          return toBackendQuestion(q, globalIdx);
        });

        let sectionPosition = 1;
        sectionQuestions.forEach(q => { q.position = sectionPosition++; });

        const sections = items
          .filter(it => it.kind === 'section')
          .map(it => (it as { kind: 'section'; section: Section; questions: Question[] }).section);

        const panelName = item.section.name ||
          item.section.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') ||
          `section_${sections.indexOf(item.section) + 1}`;

        panels.push({
          name: panelName,
          title: item.section.title || undefined,
          description: item.section.description || undefined,
          position: positionCounter++,
          questions: sectionQuestions,
        });
      }
    }

    return [{
      position: 1,
      title: 'Page 1',
      questions: ungroupedQuestions,
      panels,
    }];
  };

  const handleSave = async (saveAsNewVersion: boolean = false) => {
    if (!title || allQuestions.some((q) => !q.text)) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      let feedbackId = id;
      let versionNo = selectedVersion || 1;

      if (!id) {
        const newFeedback = await createFeedback.mutateAsync({
          title,
          status: 'draft',
        });
        feedbackId = newFeedback.id;
        versionNo = newFeedback.currentVersion || 1;
      } else {
        if (saveAsNewVersion) {
          versionNo = nextVersionNumber;
        }

        await updateFeedback.mutateAsync({
          id,
          dto: {
            title,
            status: feedback?.status || 'draft',
          },
        });
      }

      if (feedbackId) {
        await updateStructure.mutateAsync({
          id: feedbackId,
          versionNo,
          structure: {
            pages: convertQuestionsToStructure(),
            versionTitle: title,
            versionDescription: description,
          },
        });

        if (saveAsNewVersion) {
          setSelectedVersion(versionNo);
        }
      }

      if (!id && feedbackId) {
        navigate(`/feedback/edit/${feedbackId}`);
      } else {
        setSuccess(saveAsNewVersion ? `Version ${versionNo} created successfully!` : 'Feedback saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data || err?.message || 'Failed to save feedback';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to save feedback. Please try again.');
      console.error('Failed to save feedback:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!id || !selectedVersion) return;

    setIsPublishing(true);
    setError('');

    try {
      await publishVersion.mutateAsync({ id, versionNo: selectedVersion });
      setSelectedVersion(selectedVersion);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.response?.data || err?.message || 'Failed to publish version';

      if (typeof errorMsg === 'string') {
        const lines = errorMsg.split('\n');
        const firstLine = lines[0];

        if (firstLine.includes('InvalidOperationException')) {
          const match = firstLine.match(/InvalidOperationException: (.+)$/);
          if (match) {
            setError(match[1]);
          } else {
            setError('Failed to publish version. Please try again.');
          }
        } else {
          setError(errorMsg);
        }
      } else {
        setError('Failed to publish version. Please try again.');
      }
      console.error('Failed to publish version:', err);
    } finally {
      setIsPublishing(false);
    }
  };

  if (feedbackLoading || structureLoading) {
    return <LoadingSkeleton />;
  }

  const activeQuestion = getActiveQuestion();
  const activeSection = getActiveSection();

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/feedback/list')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={isSaving || isPublishing}
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {id ? 'Edit Feedback' : 'Create Feedback'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {id && !isViewingPublishedVersion && (
              <>
                <button
                  onClick={() => handleSave(false)}
                  disabled={isSaving || isPublishing}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SaveIcon className="h-4 w-4 mr-2" />
                  {isSaving && !isPublishing ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handlePublish}
                  disabled={isSaving || isPublishing}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LockIcon className="h-4 w-4 mr-2" />
                  {isPublishing ? 'Publishing...' : `Publish v${selectedVersion}`}
                </button>
              </>
            )}
            {id && (
              <button
                onClick={() => handleSave(true)}
                disabled={isSaving || isPublishing}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileTextIcon className="h-4 w-4 mr-2" />
                {isSaving && !isPublishing ? 'Saving...' : `Save as v${nextVersionNumber}`}
              </button>
            )}
            {!id && (
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SaveIcon className="h-4 w-4 mr-2" />
                {isSaving ? 'Creating...' : 'Create Feedback'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6 max-w-5xl mx-auto">
        {id && versions && versions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Version:
              </label>
              <select
                value={selectedVersion || 1}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                className="px-3 py-1.5 rounded-md text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {versions.map((version) => (
                  <option key={version.versionNo} value={version.versionNo}>
                    v{version.versionNo} {version.publishedAt ? '(Published)' : '(Draft)'}
                  </option>
                ))}
              </select>
            </div>
            {isViewingPublishedVersion ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <LockIcon className="h-3 w-3 mr-1.5" />
                Published
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                <FileTextIcon className="h-3 w-3 mr-1.5" />
                Draft
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500 rounded-r-lg p-4">
            <div className="flex items-start">
              <AlertCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500 rounded-r-lg p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-800 dark:text-green-400">{success}</p>
            </div>
          </div>
        )}

        {id && isViewingPublishedVersion && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500 rounded-r-lg p-4">
            <div className="flex items-start">
              <LockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                  This version is published and cannot be edited
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  To make changes, click "Save as v{nextVersionNumber}" to create a new draft version. The current version will remain unchanged.
                </p>
              </div>
            </div>
          </div>
        )}

        {id && !isViewingPublishedVersion && !hasVersion && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-500 rounded-r-lg p-4">
            <div className="flex items-start">
              <AlertCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
                  No published version yet
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  This feedback is in draft mode. Click "Save" to save your changes, then "Publish v{selectedVersion}" to make it available for feedback instances.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="space-y-5">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isReadOnly}
              className="w-full px-0 py-2 text-lg font-medium border-0 border-b-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:outline-none focus:ring-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
              placeholder="Untitled Feedback"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isReadOnly}
              className="w-full px-0 py-2 text-sm border-0 border-b border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:outline-none focus:ring-0 bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50 resize-none"
              rows={2}
              placeholder="Add a description..."
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Questions</h2>
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full px-2.5 py-0.5 text-xs font-medium">
                {allQuestions.length}
              </span>
            </div>
          </div>

          <DndContext
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={topLevelIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {items.map(item => {
                  if (item.kind === 'question') {
                    const globalIndex = getGlobalIndex(item.question);
                    return (
                      <SortableQuestionCard
                        key={item.question.id}
                        question={item.question}
                        globalIndex={globalIndex}
                        allQuestions={allQuestions}
                        isReadOnly={isReadOnly}
                        onRemove={handleRemoveQuestion}
                        onQuestionChange={handleQuestionChange}
                        onTypeChange={handleTypeChange}
                        onOptionChange={handleOptionChange}
                        onAddOption={handleAddOption}
                        onRemoveOption={handleRemoveOption}
                        onToggleCondition={handleToggleCondition}
                        onConditionChange={handleConditionChange}
                        onConditionValueToggle={handleConditionValueToggle}
                        onToggleFlag={handleToggleFlag}
                        onFlagChange={handleFlagChange}
                        onFlagValueToggle={handleFlagValueToggle}
                      />
                    );
                  }
                  return (
                    <SortableSectionContainer
                      key={item.section.id}
                      section={item.section}
                      questions={item.questions}
                      allQuestions={allQuestions}
                      isReadOnly={isReadOnly}
                      onSectionChange={handleSectionChange}
                      onToggleCollapse={handleToggleSectionCollapse}
                      onRemoveSection={handleRemoveSection}
                      onAddQuestion={handleAddQuestion}
                      onRemoveQuestion={handleRemoveQuestion}
                      onQuestionChange={handleQuestionChange}
                      onTypeChange={handleTypeChange}
                      onOptionChange={handleOptionChange}
                      onAddOption={handleAddOption}
                      onRemoveOption={handleRemoveOption}
                      onToggleCondition={handleToggleCondition}
                      onConditionChange={handleConditionChange}
                      onConditionValueToggle={handleConditionValueToggle}
                      onToggleFlag={handleToggleFlag}
                      onFlagChange={handleFlagChange}
                      onFlagValueToggle={handleFlagValueToggle}
                      getGlobalIndex={getGlobalIndex}
                    />
                  );
                })}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeQuestion && (
                <div className="opacity-95 shadow-2xl rotate-1 ring-2 ring-blue-400 rounded-xl">
                  <QuestionCard
                    question={activeQuestion}
                    globalIndex={getGlobalIndex(activeQuestion)}
                    allQuestions={allQuestions}
                    isReadOnly={true}
                    onRemove={() => {}}
                    onQuestionChange={() => {}}
                    onTypeChange={() => {}}
                    onOptionChange={() => {}}
                    onAddOption={() => {}}
                    onRemoveOption={() => {}}
                    onToggleCondition={() => {}}
                    onConditionChange={() => {}}
                    onConditionValueToggle={() => {}}
                    onToggleFlag={() => {}}
                    onFlagChange={() => {}}
                    onFlagValueToggle={() => {}}
                  />
                </div>
              )}
              {activeSection && (
                <div className="opacity-95 shadow-2xl rotate-1 ring-2 ring-blue-400 rounded-xl">
                  <SectionContainer
                    section={activeSection.section}
                    questions={activeSection.questions}
                    allQuestions={allQuestions}
                    isReadOnly={true}
                    onSectionChange={() => {}}
                    onToggleCollapse={() => {}}
                    onRemoveSection={() => {}}
                    onAddQuestion={() => {}}
                    onRemoveQuestion={() => {}}
                    onQuestionChange={() => {}}
                    onTypeChange={() => {}}
                    onOptionChange={() => {}}
                    onAddOption={() => {}}
                    onRemoveOption={() => {}}
                    onToggleCondition={() => {}}
                    onConditionChange={() => {}}
                    onConditionValueToggle={() => {}}
                    onToggleFlag={() => {}}
                    onFlagChange={() => {}}
                    onFlagValueToggle={() => {}}
                    getGlobalIndex={getGlobalIndex}
                  />
                </div>
              )}
            </DragOverlay>
          </DndContext>

          {!isReadOnly && (
            <div className="flex gap-3">
              <button
                onClick={() => handleAddQuestion()}
                className="flex-1 py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 transition-all flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Question
              </button>
              <button
                onClick={handleAddSection}
                className="flex-1 py-4 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/20 transition-all flex items-center justify-center text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300"
              >
                <LayoutListIcon className="h-5 w-5 mr-2" />
                Add Section
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackEditor;
